/**
 * Pure helpers for chart legend series visibility toggles.
 * Keeps at least `minVisible` keys visible (default 1) so charts never go empty.
 */

/**
 * @param {Set<string>} hiddenKeys
 * @param {string} key
 * @param {string[]} allKeys
 * @param {number} [minVisible=1]
 * @returns {{ nextHidden: Set<string>, changed: boolean, blocked: boolean }}
 */
export function toggleLegendVisibility(hiddenKeys, key, allKeys, minVisible = 1) {
  const keys = allKeys.filter(Boolean);
  if (!keys.includes(key)) {
    return { nextHidden: new Set(hiddenKeys), changed: false, blocked: false };
  }

  const nextHidden = new Set(hiddenKeys);
  const isHidden = nextHidden.has(key);

  if (isHidden) {
    nextHidden.delete(key);
    return { nextHidden, changed: true, blocked: false };
  }

  const visibleCount = keys.filter((k) => !nextHidden.has(k)).length;
  if (visibleCount <= minVisible) {
    return { nextHidden: new Set(hiddenKeys), changed: false, blocked: true };
  }

  nextHidden.add(key);
  return { nextHidden, changed: true, blocked: false };
}

/**
 * Filter items by visibility. Items need a `key` (or use getKey).
 */
export function filterVisibleItems(items, hiddenKeys, getKey = (item) => item.key) {
  if (!items?.length) return [];
  return items.filter((item) => !hiddenKeys.has(getKey(item)));
}

/**
 * Drop stale hidden keys that are no longer in the current key list.
 */
export function pruneHiddenKeys(hiddenKeys, allKeys) {
  const keySet = new Set(allKeys);
  let changed = false;
  const next = new Set();
  for (const key of hiddenKeys) {
    if (keySet.has(key)) next.add(key);
    else changed = true;
  }
  return changed ? next : hiddenKeys;
}

/**
 * Friendly aria-live status for a toggle. Prefer label map; fall back to key.
 * @param {string} key
 * @param {boolean} nowHidden
 * @param {Record<string, string>} [labels]
 */
export function legendToggleStatusMessage(key, nowHidden, labels = {}) {
  const label = labels?.[key] || key;
  return nowHidden ? `Hidden ${label}` : `Showing ${label}`;
}
